import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { TiplymeLogo, GoogleButton } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name || "there"}!`);
      navigate(u.role === "worker" ? "/app" : "/admin", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="hidden flex-col justify-between bg-brand-green p-10 text-white md:flex">
        <TiplymeLogo dark />
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">Tips that reach the people who earn them.</h2>
          <p className="mt-4 max-w-sm text-white/80">Sign in to your worker wallet or the TiplyMe admin portal.</p>
        </div>
        <p className="text-sm text-white/60">tiplyme.co.za</p>
      </div>

      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="md:hidden"><TiplymeLogo /></div>
          <h1 className="mt-6 font-heading text-2xl font-bold">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome back to TiplyMe.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="mt-1.5" placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-brand-green hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="mt-1.5" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> OR <span className="h-px flex-1 bg-border" />
          </div>
          <GoogleButton label="Sign in with Google" />

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="font-semibold text-brand-green hover:underline" data-testid="go-register-link">Create a worker account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
